import logger from '@eventbrite/client-logger';
import {
    getLocaleOverride,
    setupAsync,
    getParentLocale,
} from '@eventbrite/i18n';
import { initializeErrorReporting } from '@eventbrite/error-reporting';
import {
    initDimensionsFromState,
    trackInitialPageView,
} from '@eventbrite/site-analytics';
import { loadableReady } from '@loadable/component';
import {
    Dedupe as DedupeIntegration,
    ExtraErrorData as ExtraErrorDataIntegration,
    HttpClient as HttpClientIntegration,
} from '@sentry/integrations';
import React from 'react';
import { hydrate, render } from 'react-dom';
import { initializeStatsigSdk } from './actions/statsig';
import { initializeSplitioSdk } from './actions/splitio';
import { checkoutBFFBaseUrlForEnvironment } from './api/constants';
import App from './app';
import GenericErrorMessage from './components/errorNotification/GenericErrorMessage';
import { initDatadogRUM } from './initDatadogRUM';
import { createCheckoutStore } from './utils/store';

const root = document.getElementById('root');
const props = window.__SERVER_DATA__;
const isProd = (domain = {}) => domain.includes('eventbrite');

const main = async () => {
    if (!props) {
        throw new Error('props is missing');
    }
    if (!root) {
        throw new Error('root is null');
    }

    // __LOCALS__ is defined only when running checkout using `bolt dev`. In this situation __SERVER_DATA__ does not have app_name nor app_version
    const appName =
        props.app_name ??
        window.__LOCALS__?.application?.name
            .replace('@eventbrite/', '')
            .replace('@eventbrite/', '');
    const appVersion = props.app_version;

    const parentLocale = getParentLocale(
        getLocaleOverride() || props.env.localeInfo.locale,
    );

    const { store, initialPath, history } = createCheckoutStore({
        ...props,
        parentLocale,
    });

    try {
        await initializeStatsigSdk(store.getState(), props.stableId || null);
        store.dispatch(initializeSplitioSdk());

        initializeErrorReporting({
            metaData: {
                tags: {
                    event_id: props.event.id,
                },
            },
            integrations: [
                new DedupeIntegration(),
                new ExtraErrorDataIntegration(),
                new HttpClientIntegration({
                    failedRequestTargets: [
                        ...Object.values(checkoutBFFBaseUrlForEnvironment),
                    ],
                }),
            ],
        });
        if (parentLocale !== 'en_US') {
            await setupAsync({
                parentLocale,
                translations:
                    /* webpackChunkName: "checkout-i18n-translation" */ import(
                        `./i18n/translations/${parentLocale}.json`
                    ),
            });
        }
    } finally {
        // render app with server props to the DOM
        loadableReady(() => {
            hydrate(
                <App
                    {...props}
                    appName={appName}
                    appVersion={appVersion}
                    initialPath={initialPath}
                    store={store}
                    history={history}
                />,
                root,
            );
        });

        initDimensionsFromState(props);
        trackInitialPageView(props.gaSettings);

        if (isProd(props.env.ebDomain)) {
            initDatadogRUM({
                app: { name: appName, version: appVersion },
            });
        }
    }
};

try {
    main();
} catch (error) {
    logger.error('checkoutWidget main error:', {
        error: error.message ? error.message : error,
        props,
    });
    if (root) {
        try {
            render(<GenericErrorMessage />, root);
        } catch (e) {
            logger.error('GenericErrorMessage error:', {
                error: e,
            });
        }
    }
}
